// TecnicoVisitasModal — abre ao clicar num técnico (no mapa ou na BarraTecnicos).
// Mostra todas as OS do dia daquele técnico, com status visual e contagens.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { classificarVisita, COR_STATUS_VISUAL } from '../lib/statusVisual.js';
import { osLabel } from '../lib/visita.js';
import { visitas as visitasApi } from '../lib/api.js';
import RegistroAtendimento from './RegistroAtendimento.jsx';

const STATUS_TEC_LABEL = {
  em_rota: '🚗 Em rota', atendendo: '🔧 Atendendo', pausa: '☕ Pausa',
  ocioso: '⏸ Ocioso', parado_alerta: '⚠️ Parado', sem_gps: '○ Sem GPS',
};

function formatHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
  });
}

function formatDuracao(min) {
  if (min == null || isNaN(min)) return '';
  const sinal = min < 0 ? '-' : '+';
  const abs = Math.abs(Math.round(min));
  if (abs < 60) return `${sinal}${abs}min`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sinal}${h}h${m > 0 ? `${m.toString().padStart(2,'0')}` : ''}`;
}

export default function TecnicoVisitasModal({ tecnico, visitas, onClose }) {
  const navigate = useNavigate();

  // Ordena por janela_inicio
  const visitasOrd = useMemo(() => {
    const arr = [...(visitas || [])].sort((a, b) =>
      new Date(a.janela_inicio) - new Date(b.janela_inicio)
    );
    const agora = new Date();
    return arr.map(v => ({ ...v, _sv: classificarVisita(v, agora) }));
  }, [visitas]);

  // Contagens por status
  const resumo = useMemo(() => {
    const r = { total: visitasOrd.length, concluidas: 0, em_andamento: 0, atrasadas: 0, agendadas: 0, canceladas: 0 };
    for (const v of visitasOrd) {
      if (v._sv === 'concluida') r.concluidas++;
      else if (v._sv === 'em_andamento_no_horario' || v._sv === 'em_andamento_atrasada') r.em_andamento++;
      else if (v._sv === 'atrasada') r.atrasadas++;
      else if (v._sv === 'cancelada' || v._sv === 'nao_compareceu') r.canceladas++;
      else r.agendadas++;
    }
    return r;
  }, [visitasOrd]);

  // Estado de expansão por OS — guarda detalhe completo (incluindo materiais/relato)
  const [expandida, setExpandida] = useState(null);  // id da OS aberta (null = nenhuma)
  const [detalhes, setDetalhes] = useState({});       // { [id]: detalheVisitaCompleto }
  const [loading, setLoading] = useState({});         // { [id]: bool }

  async function toggleExpand(visitaId) {
    if (expandida === visitaId) {
      setExpandida(null);
      return;
    }
    setExpandida(visitaId);
    if (!detalhes[visitaId] && !loading[visitaId]) {
      setLoading(l => ({ ...l, [visitaId]: true }));
      try {
        const r = await visitasApi.get(visitaId);
        setDetalhes(d => ({ ...d, [visitaId]: r.data }));
      } catch (e) {
        // falha silenciosa — usuário pode tentar de novo fechando/abrindo
        console.error('Erro ao buscar detalhe da visita', e);
      } finally {
        setLoading(l => ({ ...l, [visitaId]: false }));
      }
    }
  }

  if (!tecnico) return null;

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal-content tec-visitas-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="tvm-header">
          <div className="tvm-header-left">
            {tecnico.foto_url ? (
              <img src={tecnico.foto_url} alt={tecnico.apelido}
                   style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
                            border: `3px solid ${tecnico.avatar_cor || '#9ca3af'}` }}/>
            ) : (
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: tecnico.avatar_cor || '#9ca3af',
                color: 'white', fontWeight: 700, fontSize: 20,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '3px solid white', boxShadow: '0 0 0 2px ' + (tecnico.avatar_cor || '#9ca3af'),
              }}>
                {tecnico.iniciais || (tecnico.apelido || '?').substring(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <h2 style={{ margin: 0, fontSize: 18 }}>{tecnico.apelido || tecnico.nome}</h2>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {tecnico.nome}
                {tecnico.veiculo_placa && (
                  <span style={{ marginLeft: 8, padding: '1px 6px', background: '#f3f4f6', borderRadius: 4, fontFamily: 'monospace' }}>
                    🚗 {tecnico.veiculo_placa}
                  </span>
                )}
                {tecnico.status_calc && (
                  <span style={{ marginLeft: 8 }}>
                    · {STATUS_TEC_LABEL[tecnico.status_calc] || tecnico.status_calc}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="tvm-close" aria-label="Fechar">✕</button>
        </div>

        {/* KPI cards */}
        <div className="tvm-kpis">
          <div className="tvm-kpi">
            <div className="tvm-kpi-n">{resumo.total}</div>
            <div className="tvm-kpi-l">OS do dia</div>
          </div>
          <div className="tvm-kpi">
            <div className="tvm-kpi-n" style={{ color: '#22c55e' }}>{resumo.concluidas}</div>
            <div className="tvm-kpi-l">Concluídas</div>
          </div>
          <div className="tvm-kpi">
            <div className="tvm-kpi-n" style={{ color: '#3b82f6' }}>{resumo.em_andamento}</div>
            <div className="tvm-kpi-l">Em andamento</div>
          </div>
          <div className="tvm-kpi">
            <div className="tvm-kpi-n" style={{ color: '#ef4444' }}>{resumo.atrasadas}</div>
            <div className="tvm-kpi-l">Atrasadas</div>
          </div>
          <div className="tvm-kpi">
            <div className="tvm-kpi-n" style={{ color: '#94a3b8' }}>{resumo.agendadas}</div>
            <div className="tvm-kpi-l">Agendadas</div>
          </div>
        </div>

        {/* Lista de OS */}
        <div className="tvm-list">
          {visitasOrd.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              Nenhuma OS atribuída a este técnico hoje.
            </div>
          )}
          {visitasOrd.map(v => {
            const cor = COR_STATUS_VISUAL[v._sv] || COR_STATUS_VISUAL.agendada;
            let atraso = null;
            if (v.dt_inicio_atendimento && v.janela_inicio) {
              atraso = (new Date(v.dt_inicio_atendimento) - new Date(v.janela_inicio)) / 60000;
            }
            const isOpen = expandida === v.id;
            const detalhe = detalhes[v.id];
            const isLoading = loading[v.id];
            return (
              <div key={v.id}>
                <div className="tvm-item"
                     onClick={() => toggleExpand(v.id)}
                     style={{ borderLeft: `4px solid ${cor.borda}`, background: isOpen ? '#f9fafb' : undefined }}>
                  <div className="tvm-item-hora">
                    <strong>{formatHora(v.janela_inicio)}</strong>
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>até {formatHora(v.janela_fim)}</span>
                  </div>
                  <div className="tvm-item-info">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 13 }}>OS {osLabel(v)}</strong>
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        padding: '2px 6px', borderRadius: 3,
                        background: cor.fundo, color: cor.texto,
                      }}>
                        {cor.label}
                      </span>
                      {atraso != null && Math.abs(atraso) >= 5 && Math.abs(atraso) <= 480 && (
                        <span style={{ fontSize: 10, color: atraso > 10 ? '#dc2626' : '#059669', fontFamily: 'monospace' }}>
                          {formatDuracao(atraso)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>
                      {v.cliente_nome || '(sem cliente)'}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                      {v.cliente_bairro || ''}{v.cliente_bairro && v.tipo_os ? ' · ' : ''}{v.tipo_os || ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 18, color: '#9ca3af', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</div>
                </div>

                {/* Expansão inline com registro do atendimento */}
                {isOpen && (
                  <div style={{ padding: '8px 16px 12px 16px', background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                    {isLoading && (
                      <div className="muted" style={{ padding: 10, fontSize: 12 }}>Carregando registro…</div>
                    )}
                    {!isLoading && detalhe && (
                      <>
                        <RegistroAtendimento visita={detalhe} />
                        <div style={{ marginTop: 8, textAlign: 'right' }}>
                          <button className="btn small secondary"
                                  onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/visitas/${v.id}`); }}>
                            Ver detalhes completos →
                          </button>
                        </div>
                      </>
                    )}
                    {!isLoading && !detalhe && (
                      <div className="muted" style={{ padding: 10, fontSize: 12 }}>Sem dados disponíveis pra esta OS.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
